import React, { useEffect, useMemo, useRef } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import { TicketComment } from '~/components/Ticket/TicketDetails/TicketComments/TicketComment';
import useAsyncActionStatus from '~/hooks/useAsyncActionStatus';
import { createTicketComment, getCommentTypes, getTicketComments } from '~/store/tickets/actions';
import { findCommentTypeId, COMMENT_TYPE } from '~/components/Ticket/drawers/ResolveTicket/constants';

import { Alert, Button, Flex, Form, RichText, Shimmer, Typography } from '~/ui/components';
import validateRichText from '~/common/Validations/validateRichText';
import { Section, Wrapper } from './CommentsList.styles';

interface CommentsListProps {
  loggedUser: any;
  placeholder?: string;
  maxHeight?: number;
  ticketId?: string;
  refreshKey?: number;
  /** respiro lateral (CORE-3512) — usado pela aba Comentários do workspace */
  padded?: boolean;
}

function CommentsList({
  loggedUser,
  placeholder = 'Escreva um comentário',
  maxHeight,
  ticketId,
  refreshKey = 0,
  padded = false,
}: Readonly<CommentsListProps>) {
  const formRef = useRef<any>(null);

  const getTicketCommentsControl = useAsyncActionStatus(getTicketComments, ticketId);
  const isLoadingComments = getTicketCommentsControl.loading;
  // The Comentários tab lists every message (WhatsApp, public, reserved and backoffice),
  // each with its type tag. Editing is restricted to backoffice notes (see TicketComment).
  const comments = getTicketCommentsControl.result;

  const createTicketCommentControl = useAwaitControl(createTicketComment);
  const createTicketCommentSuccessfully = createTicketCommentControl.isSuccessful();

  const getCommentTypesControl = useAwaitControl(getCommentTypes);
  const commentTypes = getCommentTypesControl.result();

  const inputs = useMemo(
    () => [
      {
        id: 'description',
        input: <RichText small showActionButton placeholder={placeholder} />,
        options: {
          rules: [
            { required: true, message: 'Este campo é obrigatório!' },
            { validator: validateRichText, message: 'Este campo é obrigatório!' },
          ],
        },
      },
    ],
    [],
  );

  const skeleton = useMemo(
    () => (
      <>
        {Array.from({ length: 4 }).map((_, index) => (
          <Section key={index} borderBottom>
            <Flex direction="column" width="100%">
              <Shimmer width={109} height={20} mb={4} />
              <Shimmer width={136} height={20} mb={16} />
              <Shimmer width={300} height={20} />
            </Flex>
          </Section>
        ))}
      </>
    ),
    [],
  );

  const handleSubmit = (values: any) => {
    if (values.description) {
      // Comments written here are internal (backoffice) notes, not customer-facing messages.
      const commentData = {
        ticketId,
        description: values.description,
        authorNameId: loggedUser?.id,
        authorName: loggedUser?.name,
        publicComment: true,
        typeId: findCommentTypeId(commentTypes, COMMENT_TYPE.BACKOFFICE),
      };
      createTicketCommentControl.start(commentData);
    }
  };

  useEffect(() => {
    if (!commentTypes?.length) getCommentTypesControl.start();
  }, [commentTypes?.length]);

  useEffect(() => {
    getTicketCommentsControl.start(ticketId);

    if (createTicketCommentSuccessfully) {
      formRef.current?.setFieldsValue({ description: null });
    }
  }, [createTicketCommentSuccessfully, refreshKey, ticketId]);

  return (
    <Wrapper $padded={padded}>
      <Section gap={4}>
        <Typography type="paragraphSmall" weight={600}>
          Comentar
        </Typography>
        <Form ref={formRef} onSubmit={handleSubmit} loading={isLoadingComments} inputs={inputs}>
          <Button
            className="submit-button"
            type="submit"
            title="Enviar"
            variant="outline"
            size="sm"
            color="primary"
            icon="Send"
            rounded
          />
        </Form>
      </Section>
      <Section gap={16} height={maxHeight}>
        <Flex direction="column" gap={16} width="100%">
          {Array.isArray(comments) &&
            !isLoadingComments &&
            comments.map((comment) => <TicketComment key={comment.id} comment={comment} ticketId={ticketId} />)}
          {isLoadingComments && skeleton}
          {!comments.length && !isLoadingComments && (
            <Alert label="Nenhum comentário encontrado." status="neutral" fullWidth />
          )}
        </Flex>
      </Section>
    </Wrapper>
  );
}

export default CommentsList;
